import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, Plus, Trash2, ClipboardList, TrendingUp, TrendingDown, Trophy, Pencil, Upload, Download, FileText } from "lucide-react";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import DateField from "@/components/app/DateField";

const POSITIONS = ["POR", "DEF", "MED", "DEL"];

export default function TeamDetail() {
  const { teamId } = useParams();
  const { user } = useAuth();
  const [team, setTeam] = useState(null);
  const [category, setCategory] = useState(null);
  const [players, setPlayers] = useState([]);
  const [jornadas, setJornadas] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [staff, setStaff] = useState([]);           // coach + delegado of this club
  const [editStaff, setEditStaff] = useState(null); // {selectedIds:[...]} while editing
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPlayer, setShowPlayer] = useState(false);
  const [editingPlayer, setEditingPlayer] = useState(null);
  const [pForm, setPForm] = useState({ name: "", jersey_number: "", position: "MED", dominant_foot: "Derecho", birthdate: "", parent_contacts: [] });
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const a = await api.get(`/analytics/team/${teamId}`);
      setAnalytics(a.data);
      setTeam(a.data.team);
      const [p, j, cats] = await Promise.all([
        api.get(`/players?team_id=${teamId}`),
        api.get(`/jornadas?team_id=${teamId}`),
        api.get("/categories"),
      ]);
      setPlayers(p.data);
      setJornadas(j.data);
      setCategory(cats.data.find(c => c.id === a.data.team.category_id));
      // Load club staff (coaches/delegados) for the coaches-assignment panel.
      // Only admin-level roles can hit /users; ignore silently otherwise.
      try {
        const u = await api.get("/users");
        setStaff((u.data || []).filter(x => ["coach", "delegado"].includes(x.role)));
      } catch { /* non-admins won't see the panel */ }
    } catch (e) {
      const status = e.response?.status;
      if (status === 403) setError("No tienes acceso a este equipo.");
      else if (status === 404) setError("Equipo no encontrado.");
      else setError(formatApiError(e.response?.data?.detail) || "Error al cargar el equipo");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [teamId]);

  const canEdit = user && ["super_admin", "club_admin", "coordinator", "director_deportivo"].includes(user.role);
  const isMyTeam = team && team.coach_ids?.includes(user?.id);

  const saveCoaches = async (nextIds) => {
    try {
      await api.put(`/teams/${teamId}/coaches`, { coach_ids: nextIds });
      toast.success("Cuerpo técnico actualizado");
      setEditStaff(null);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openAddPlayer = () => {
    setEditingPlayer(null);
    setPForm({ name: "", jersey_number: "", position: "MED", dominant_foot: "Derecho", birthdate: "", parent_contacts: [] });
    setShowPlayer(true);
  };
  const openEditPlayer = (p) => {
    setEditingPlayer(p);
    setPForm({
      name: p.name || "", jersey_number: p.jersey_number ?? "",
      position: p.position || "MED", dominant_foot: p.dominant_foot || "Derecho",
      birthdate: p.birthdate || "", parent_contacts: p.parent_contacts || [],
    });
    setShowPlayer(true);
  };

  const addPlayer = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...pForm, team_id: teamId,
        jersey_number: pForm.jersey_number ? Number(pForm.jersey_number) : null,
      };
      if (editingPlayer) {
        await api.put(`/players/${editingPlayer.id}`, payload);
        toast.success("Jugador actualizado");
      } else {
        await api.post("/players", payload);
        toast.success("Jugador añadido");
      }
      setShowPlayer(false); setEditingPlayer(null);
      setPForm({ name: "", jersey_number: "", position: "MED", dominant_foot: "Derecho", birthdate: "", parent_contacts: [] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removePlayer = async (id) => {
    if (!window.confirm("¿Eliminar este jugador?")) return;
    try {
      await api.delete(`/players/${id}`);
      toast.success("Jugador eliminado");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportPlayers = async () => {
    try {
      const res = await api.get(`/teams/${teamId}/players.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `jugadores-${team?.name?.replace(/\s+/g, "-").toLowerCase() || "equipo"}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get(`/teams/players/template.csv`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "plantilla-jugadores.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const previewImport = async (file) => {
    setImportFile(file);
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/teams/${teamId}/players/import?dry_run=true`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportPreview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setImportFile(null);
    } finally {
      setImporting(false);
    }
  };

  const confirmImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", importFile);
      const { data } = await api.post(`/teams/${teamId}/players/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.inserted} jugador${data.inserted === 1 ? "" : "es"} importado${data.inserted === 1 ? "" : "s"}`);
      setImportPreview(null);
      setImportFile(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setImporting(false);
    }
  };

  if (loading) return <div className="text-muted-foreground text-sm">Cargando…</div>;
  if (error) {
    const backLink = user?.role === "coach" ? "/coach" : (user?.role === "coordinator" ? "/coordinator/teams" : "/club/teams");
    return (
      <div className="max-w-lg mx-auto text-center py-20">
        <Link to={backLink} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-6 link-underline">
          <ArrowLeft size={14} /> Volver
        </Link>
        <div data-testid="team-access-error" className="border border-destructive p-8">
          <div className="overline text-destructive">Acceso denegado</div>
          <div className="font-heading font-black text-3xl uppercase tracking-tighter mt-2">{error}</div>
          <p className="mt-3 text-sm text-muted-foreground">Verifica con el administrador de tu club si necesitas acceso a este equipo.</p>
        </div>
      </div>
    );
  }
  if (!team) return null;

  const totals = analytics?.totals || {};
  const chartData = (analytics?.timeline || []).map(t => ({
    name: `J${t.jornada_number || "?"}`,
    "GF": t.gols_favor,
    "GC": t.gols_contra,
  }));

  const backLink = user?.role === "coach" ? "/coach" : (user?.role === "coordinator" ? "/coordinator/teams" : "/club/teams");

  return (
    <div>
      <Link to={backLink} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary mb-4 link-underline">
        <ArrowLeft size={14} /> Volver
      </Link>

      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="overline flex items-center gap-2"><Trophy size={12} /> {category?.name} · {category?.template_key} · Temp. {team.season}</div>
          <h1 className="font-heading font-black text-5xl md:text-6xl tracking-tighter uppercase mt-2">{team.name}</h1>
        </div>
        {(isMyTeam || canEdit) && (
          <div className="flex gap-2">
            <Link to={`/coach/jornadas/new?team=${teamId}`} data-testid="register-jornada-btn" className="btn-volt">
              <Plus size={16} /> Registrar jornada
            </Link>
          </div>
        )}
      </div>

      <div className="grid grid-tactical grid-cols-2 md:grid-cols-4 lg:grid-cols-8 mb-8">
        <Kpi label="Partidos" value={totals.partidos ?? 0} />
        <Kpi label="Entrenos" value={totals.entrenos ?? 0} />
        <Kpi label="Victorias" value={totals.victorias ?? 0} />
        <Kpi label="Empates" value={totals.empates ?? 0} />
        <Kpi label="Derrotas" value={totals.derrotas ?? 0} />
        <Kpi label="GF" value={totals.gols_favor ?? 0} accent />
        <Kpi label="GC" value={totals.gols_contra ?? 0} destructive />
        <Kpi label="Jugadores" value={players.length} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <div className="lg:col-span-2 border border-border">
          <div className="p-4 border-b border-border overline">Evolución por jornada</div>
          <div className="p-4 h-64">
            {chartData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="name" stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11 }} />
                  <YAxis stroke="rgba(255,255,255,0.4)" tick={{ fontSize: 11 }} />
                  <Tooltip contentStyle={{ backgroundColor: '#121212', border: '1px solid #27272A', borderRadius: 4, fontSize: 12 }} />
                  <Bar dataKey="GF" fill="#0055FF" />
                  <Bar dataKey="GC" fill="#FF3B30" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-sm text-muted-foreground">Sin jornadas registradas todavía.</div>
            )}
          </div>
        </div>

        <div className="border border-border">
          <div className="p-4 border-b border-border overline">Últimas jornadas</div>
          <div className="divide-y divide-border max-h-64 overflow-y-auto">
            {jornadas.slice(0, 10).map((j) => (
              <div key={j.id} data-testid={`jornada-item-${j.id}`} className="p-3 hover:bg-white/[0.02]">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-xs font-mono text-muted-foreground">{j.date}</div>
                    <div className="text-sm font-heading font-bold uppercase">
                      {j.session_type === "partido" ? (
                        <>J{j.jornada_number} vs {j.opponent || "—"}</>
                      ) : "Entreno"}
                    </div>
                  </div>
                  {j.session_type === "partido" && j.team_stats && (
                    <div className="text-right">
                      <div className={`metric-number text-xl ${j.team_stats.resultado === 'V' ? 'text-primary' : j.team_stats.resultado === 'D' ? 'text-destructive' : ''}`}>
                        {j.team_stats.gols_favor}-{j.team_stats.gols_contra}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {jornadas.length === 0 && <div className="p-4 text-center text-muted-foreground text-sm">Sin jornadas.</div>}
          </div>
        </div>
      </div>

      {/* Coaching staff panel — bidireccional counterpart of the Staff CRUD.
          Admins/coordinators/DDs can assign coaches & delegados to this team from here. */}
      <div className="border border-border mb-8" data-testid="team-staff-panel">
        <div className="p-4 border-b border-border flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="overline">Cuerpo técnico</div>
            <div className="text-xs font-mono text-muted-foreground mt-1">
              Entrenadores y delegados asignados a este equipo · {(team.coach_ids || []).length} persona(s)
            </div>
          </div>
          {canEdit && (
            <button
              data-testid="edit-team-coaches-btn"
              onClick={() => setEditStaff({ selectedIds: [...(team.coach_ids || [])] })}
              className="h-9 px-4 border border-border hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-widest inline-flex items-center gap-2"
            ><Pencil size={13} /> Editar</button>
          )}
        </div>
        {(team.coach_ids || []).length === 0 ? (
          <div className="p-6 text-center text-muted-foreground text-sm">
            Sin cuerpo técnico asignado.
            {canEdit && <span className="block mt-1 text-[11px] opacity-70">Pulsa &quot;Editar&quot; para asignar entrenadores o delegados.</span>}
          </div>
        ) : (
          <div className="p-4 flex flex-wrap gap-2">
            {(team.coach_ids || []).map(cid => {
              const u = staff.find(s => s.id === cid);
              if (!u) return (
                <span key={cid} data-testid={`team-coach-badge-${cid}`} className="px-2 py-1 border border-border text-xs font-mono text-muted-foreground">Usuario desconocido</span>
              );
              return (
                <span key={cid} data-testid={`team-coach-badge-${cid}`} className={`px-2 py-1 border text-xs font-mono ${u.role === "coach" ? "border-primary text-primary" : "border-emerald-500/60 text-emerald-400"}`}>
                  <span className="font-heading font-bold">{u.name}</span>
                  <span className="opacity-70 ml-1">· {u.role === "coach" ? "Entrenador" : "Delegado"}</span>
                  {u.phone && <span className="opacity-60 ml-1">· {u.phone}</span>}
                </span>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="overline">Plantilla</div>
          <h2 className="font-heading font-bold text-2xl uppercase tracking-tight mt-1">Jugadores ({players.length})</h2>
        </div>
        {(isMyTeam || canEdit) && (
          <div className="flex gap-2 flex-wrap">
            <button data-testid="players-export-btn" onClick={exportPlayers}
              className="h-10 px-4 border-2 border-white/15 hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-widest flex items-center gap-2">
              <Download size={14} /> Exportar
            </button>
            <button data-testid="players-import-btn" onClick={() => document.getElementById("players-import-file").click()}
              className="h-10 px-4 border-2 border-white/15 hover:border-primary hover:text-primary text-xs uppercase font-heading tracking-widest flex items-center gap-2">
              <Upload size={14} /> Importar
            </button>
            <input id="players-import-file" data-testid="players-import-file" type="file" accept=".csv,text/csv" className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) previewImport(f); e.target.value = ""; }} />
            <button data-testid="add-player-btn" onClick={openAddPlayer} className="btn-volt"><Plus size={16} /> Añadir</button>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {players.map((p) => (
          <div key={p.id} data-testid={`player-card-${p.id}`} className="card-tactical p-4">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 border border-border flex items-center justify-center font-heading font-black text-lg">
                {p.jersey_number ?? "—"}
              </div>
              <div className="flex-1">
                <Link to={`/player/${p.id}`} className="font-heading font-bold uppercase tracking-tight hover:text-primary link-underline">{p.name}</Link>
                <div className="text-xs text-muted-foreground font-mono mt-1">{p.position || "—"} · {p.dominant_foot || "—"}</div>
              </div>
              {(isMyTeam || canEdit) && (
                <div className="flex gap-1">
                  <button data-testid={`edit-player-${p.id}`} onClick={() => openEditPlayer(p)} className="text-muted-foreground hover:text-primary"><Pencil size={13} /></button>
                  <button data-testid={`del-player-${p.id}`} onClick={() => removePlayer(p.id)} className="text-muted-foreground hover:text-destructive"><Trash2 size={14} /></button>
                </div>
              )}
            </div>
          </div>
        ))}
        {players.length === 0 && (
          <div className="col-span-full text-center py-8 border border-dashed border-border text-muted-foreground text-sm">Sin jugadores todavía.</div>
        )}
      </div>

      {(isMyTeam || canEdit) && (
        <div className="mt-3 text-[11px] text-muted-foreground">
          <button data-testid="template-download-btn" onClick={downloadTemplate} className="inline-flex items-center gap-1 hover:text-primary link-underline">
            <FileText size={11}/> Descargar plantilla CSV vacía
          </button>
          <span className="mx-2">·</span>
          <span>Importa desde Excel guardando el archivo como CSV (UTF-8, separador ;).</span>
        </div>
      )}

      {importPreview && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="import-preview-modal">
          <div className="bg-card border border-border w-full max-w-lg p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div>
              <div className="overline">Vista previa</div>
              <div className="font-heading font-black text-2xl uppercase tracking-tight">Importar jugadores</div>
              <div className="text-xs text-muted-foreground mt-1">{importFile?.name}</div>
            </div>

            <div className="grid grid-cols-3 gap-3 text-center">
              <div className="border border-white/10 p-3">
                <div className="overline">Filas</div>
                <div className="font-heading font-black text-2xl mt-1">{importPreview.parsed_rows}</div>
              </div>
              <div className="border border-emerald-500/40 bg-emerald-500/5 p-3">
                <div className="overline text-emerald-400">Válidos</div>
                <div className="font-heading font-black text-2xl mt-1 text-emerald-400">{importPreview.valid_players}</div>
              </div>
              <div className={`border p-3 ${importPreview.errors.length ? "border-red-500/40 bg-red-500/5" : "border-white/10"}`}>
                <div className={`overline ${importPreview.errors.length ? "text-red-400" : ""}`}>Errores</div>
                <div className={`font-heading font-black text-2xl mt-1 ${importPreview.errors.length ? "text-red-400" : ""}`}>{importPreview.errors.length}</div>
              </div>
            </div>

            {importPreview.sample?.length > 0 && (
              <div>
                <div className="overline mb-2">Muestra (primeros 5)</div>
                <div className="border border-border divide-y divide-border text-xs">
                  {importPreview.sample.map((s, i) => (
                    <div key={i} className="p-2 flex items-center gap-3 font-mono">
                      <span className="w-8 text-muted-foreground">#{s.jersey_number ?? "—"}</span>
                      <span className="flex-1">{s.name}</span>
                      <span className="text-muted-foreground">{s.position || "—"}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {importPreview.errors.length > 0 && (
              <div>
                <div className="overline mb-2 text-red-400">Errores detectados</div>
                <div className="border border-red-500/20 max-h-32 overflow-y-auto text-xs">
                  {importPreview.errors.map((e, i) => (
                    <div key={i} className="p-2 border-b border-red-500/10 font-mono">Fila {e.row}: {e.error}</div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button data-testid="import-cancel-btn" onClick={() => { setImportPreview(null); setImportFile(null); }}
                className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
              <button data-testid="import-confirm-btn" onClick={confirmImport} disabled={importing || !importPreview.valid_players}
                className="btn-volt disabled:opacity-50">
                {importing ? "Importando…" : `Importar ${importPreview.valid_players} jugadores`}
              </button>
            </div>
          </div>
        </div>
      )}

      {showPlayer && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={addPlayer} className="bg-card border border-border w-full max-w-md p-6 space-y-4 max-h-[92vh] overflow-y-auto">
            <div>
              <div className="overline">{editingPlayer ? "Editar" : "Nuevo"}</div>
              <div className="font-heading font-black text-2xl uppercase tracking-tight">{editingPlayer ? "Actualizar jugador" : "Añadir jugador"}</div>
            </div>
            <div>
              <label className="overline block mb-1">Nombre *</label>
              <input required data-testid="pl-name-input" value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">Dorsal</label>
                <input type="number" data-testid="pl-num-input" value={pForm.jersey_number} onChange={(e) => setPForm({ ...pForm, jersey_number: e.target.value })} className="w-full h-10 bg-transparent border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none" />
              </div>
              <div>
                <label className="overline block mb-1">Posición</label>
                <select data-testid="pl-pos-select" value={pForm.position} onChange={(e) => setPForm({ ...pForm, position: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                  {POSITIONS.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline block mb-1">Pie dominante</label>
                <select value={pForm.dominant_foot} onChange={(e) => setPForm({ ...pForm, dominant_foot: e.target.value })} className="w-full h-10 bg-card border border-border px-3 font-mono text-sm focus:border-primary focus:outline-none">
                  <option>Derecho</option><option>Zurdo</option><option>Ambidiestro</option>
                </select>
              </div>
              <div>
                <label className="overline block mb-1">Fecha nacimiento</label>
                <DateField value={pForm.birthdate || ""} onChange={(v) => setPForm({ ...pForm, birthdate: v })} testid="pl-birthdate" placeholder="Selecciona fecha" />
              </div>
            </div>
            <div className="pt-4 border-t border-border">
              <div className="flex items-center justify-between mb-2">
                <div className="overline">Contactos de padres</div>
                <button type="button" data-testid="add-parent-btn" onClick={() => setPForm({ ...pForm, parent_contacts: [...pForm.parent_contacts, { name: "", phone: "", email: "", relation: "" }] })} className="text-xs text-primary link-underline flex items-center gap-1"><Plus size={11} /> Añadir contacto</button>
              </div>
              {pForm.parent_contacts.map((pc, i) => (
                <div key={i} className="border border-border p-3 mb-2 space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Nombre" value={pc.name} onChange={(e) => { const arr = [...pForm.parent_contacts]; arr[i] = { ...pc, name: e.target.value }; setPForm({ ...pForm, parent_contacts: arr }); }} className="h-9 bg-transparent border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none" />
                    <input placeholder="Relación (madre/padre)" value={pc.relation} onChange={(e) => { const arr = [...pForm.parent_contacts]; arr[i] = { ...pc, relation: e.target.value }; setPForm({ ...pForm, parent_contacts: arr }); }} className="h-9 bg-transparent border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input placeholder="Teléfono" value={pc.phone} onChange={(e) => { const arr = [...pForm.parent_contacts]; arr[i] = { ...pc, phone: e.target.value }; setPForm({ ...pForm, parent_contacts: arr }); }} className="h-9 bg-transparent border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none" />
                    <input placeholder="Email" type="email" value={pc.email} onChange={(e) => { const arr = [...pForm.parent_contacts]; arr[i] = { ...pc, email: e.target.value }; setPForm({ ...pForm, parent_contacts: arr }); }} className="h-9 bg-transparent border border-border px-2 font-mono text-xs focus:border-primary focus:outline-none" />
                  </div>
                  <button type="button" onClick={() => { const arr = pForm.parent_contacts.filter((_, j) => j !== i); setPForm({ ...pForm, parent_contacts: arr }); }} className="text-[10px] text-destructive link-underline">Quitar</button>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => { setShowPlayer(false); setEditingPlayer(null); }} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
              <button type="submit" data-testid="submit-player-btn" className="btn-volt">{editingPlayer ? "Guardar" : "Añadir"}</button>
            </div>
          </form>
        </div>
      )}

      {editStaff && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="edit-team-coaches-modal">
          <div className="bg-card border border-border w-full max-w-md p-6 space-y-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-start justify-between">
              <div>
                <div className="overline">Editar cuerpo técnico</div>
                <div className="font-heading font-black text-xl uppercase tracking-tight mt-1">{team.name}</div>
                <div className="text-[11px] font-mono text-muted-foreground mt-1">{editStaff.selectedIds.length} seleccionado(s)</div>
              </div>
              <button onClick={() => setEditStaff(null)} className="p-1 hover:text-primary">✕</button>
            </div>
            {staff.length === 0 ? (
              <div className="text-center text-sm text-muted-foreground p-6 border border-dashed border-border">
                Aún no hay entrenadores ni delegados en el club.<br />
                <span className="text-[11px] opacity-70">Añádelos desde <Link to="/club/staff" className="text-primary hover:underline">Staff</Link>.</span>
              </div>
            ) : (
              <div className="border border-border max-h-72 overflow-y-auto divide-y divide-border/40">
                {staff.map(u => {
                  const checked = editStaff.selectedIds.includes(u.id);
                  return (
                    <label key={u.id} data-testid={`team-coach-opt-${u.id}`} className={`flex items-center gap-3 p-3 cursor-pointer text-sm ${checked ? "bg-primary/5" : "hover:bg-white/[0.02]"}`}>
                      <input type="checkbox" checked={checked} onChange={(e) => {
                        setEditStaff(s => ({
                          selectedIds: e.target.checked
                            ? [...s.selectedIds, u.id]
                            : s.selectedIds.filter(x => x !== u.id),
                        }));
                      }} className="accent-primary" />
                      <div className="flex-1 min-w-0">
                        <div className="font-heading font-bold uppercase tracking-tight truncate">{u.name}</div>
                        <div className="text-[10px] font-mono text-muted-foreground">
                          {u.role === "coach" ? "Entrenador" : "Delegado"}
                          {u.phone && <span> · {u.phone}</span>}
                          {u.email && <span> · {u.email}</span>}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
            <div className="flex justify-end gap-3 pt-2">
              <button type="button" onClick={() => setEditStaff(null)} className="h-10 px-5 border border-border text-sm uppercase font-heading tracking-wider">Cancelar</button>
              <button type="button" data-testid="save-team-coaches-btn" onClick={() => saveCoaches(editStaff.selectedIds)} className="btn-volt">Guardar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, accent, destructive }) {
  return (
    <div className="p-4">
      <div className="overline">{label}</div>
      <div className={`metric-number text-3xl mt-2 ${accent ? "text-primary" : destructive ? "text-destructive" : ""}`}>{value}</div>
    </div>
  );
}
